/**
 * Remote -> TV commands. Seeded VERBATIM from spot-client
 * `remote-control/constants.ts` `COMMANDS` — the string values are the existing
 * wire protocol and MUST NOT drift (they map 1:1 onto Jitsi iframe-API
 * `executeCommand(...)` calls the TV performs). New room features generally add a
 * constant here + a payload type + a switch case on the TV (S-effort), so this
 * list is expected to grow append-only.
 */
export const COMMANDS = {
    /** Adjust the volume 'up' or 'down'. */
    ADJUST_VOLUME: 'adjustVolume',

    /** Proceed to a given meeting name or meeting URL. */
    GO_TO_MEETING: 'goToMeeting',

    /** Grant recording consent for the current meeting, optionally unmuting devices. */
    GRANT_RECORDING_CONSENT: 'grantRecordingConsent',

    /** End the current meeting. */
    HANG_UP: 'hangup',

    /** Play touch tones (DTMF) into the meeting for an IVR. */
    SEND_TOUCH_TONES: 'sendTouchTones',

    /** Set audio mute on or off. */
    SET_AUDIO_MUTE: 'setAudioMute',

    /** Start or stop room (file) recording on the TV's meeting. Moderator-gated. */
    SET_RECORDING: 'setRecording',

    /** Set raised or lowered hand. */
    SET_RAISE_HAND: 'setRaiseHand',

    /** Start or stop screensharing. */
    SET_SCREENSHARING: 'setScreensharing',

    /** Enter or exit tile-view layout. */
    SET_TILE_VIEW: 'setTileView',

    /** Show or hide the whiteboard. */
    SET_WHITEBOARD: 'setWhiteboard',

    /** Set video mute on or off. */
    SET_VIDEO_MUTE: 'setVideoMute',

    /** Send the meeting rating and entered feedback. */
    SUBMIT_FEEDBACK: 'submitFeedback',

    /** Send the password needed to join a locked meeting. */
    SUBMIT_PASSWORD: 'submitPassword',

    /** Show or hide the meeting's subtitles/captions pane on the TV. */
    SET_SUBTITLES: 'setSubtitles',

    /** Pin a participant to the TV's main stage (empty id unpins everyone). */
    PIN_PARTICIPANT: 'pinParticipant',

    /** Mute everyone else in the meeting. Moderator-gated. */
    MUTE_EVERYONE: 'muteEveryone',

    /** Enable or disable the meeting lobby (knocking). Moderator-gated. */
    SET_LOBBY: 'setLobby',

    /**
     * Send a text chat message into the meeting. NOT moderator-gated — any
     * participant may chat. The Remote composes; the TV surfaces the thread on the
     * room screen so the room can read it.
     */
    SEND_CHAT_MESSAGE: 'sendChatMessage',

    /**
     * Open or close the meeting's chat panel on the TV, independent of sending a
     * message. NOT moderator-gated. Lets the Remote put the room's conversation up
     * on the big screen (or take it down) without composing — the read-only twin of
     * SEND_CHAT_MESSAGE, which only ever opens the panel as a side effect of a send.
     */
    SET_CHAT_OPEN: 'setChatOpen'
} as const;

/** Every valid command name (the wire string). */
export type CommandName = (typeof COMMANDS)[keyof typeof COMMANDS];

/**
 * Per-command payloads. Kept deliberately loose where the legacy protocol was
 * loose (it forwarded arbitrary args to the Jitsi iframe API); Tier 1 may tighten
 * individual shapes, but the keys here are the frozen names.
 */
export interface CommandPayloads {
    [COMMANDS.ADJUST_VOLUME]: { direction: 'up' | 'down' };
    [COMMANDS.GO_TO_MEETING]: {
        meetingName: string;
        /** Optional join-time options (password, mute state, display name, ...). */
        options?: Record<string, unknown>;
    };
    [COMMANDS.GRANT_RECORDING_CONSENT]: { unmute?: boolean };
    [COMMANDS.HANG_UP]: { skipFeedback?: boolean } | undefined;
    [COMMANDS.SEND_TOUCH_TONES]: { tones: string };
    [COMMANDS.SET_AUDIO_MUTE]: { mute: boolean };
    [COMMANDS.SET_RECORDING]: { on: boolean };
    [COMMANDS.SET_RAISE_HAND]: { raised: boolean };
    [COMMANDS.SET_SCREENSHARING]: { on: boolean };
    [COMMANDS.SET_TILE_VIEW]: { tileView: boolean };
    [COMMANDS.SET_WHITEBOARD]: { visible: boolean };
    [COMMANDS.SET_VIDEO_MUTE]: { mute: boolean };
    [COMMANDS.SUBMIT_FEEDBACK]: { score: number; message?: string };
    [COMMANDS.SUBMIT_PASSWORD]: { password: string };
    [COMMANDS.SET_SUBTITLES]: { on: boolean };
    /** Empty `participantId` unpins everyone. */
    [COMMANDS.PIN_PARTICIPANT]: { participantId: string };
    [COMMANDS.MUTE_EVERYONE]: { mediaType?: 'audio' | 'video' };
    [COMMANDS.SET_LOBBY]: { enabled: boolean };
    /** The chat text to send into the meeting (public message). */
    [COMMANDS.SEND_CHAT_MESSAGE]: { text: string };
    /** Whether the TV's chat panel should be open (`true`) or closed (`false`). */
    [COMMANDS.SET_CHAT_OPEN]: { open: boolean };
}

/**
 * A fully-typed command + its payload, as a discriminated union over the command
 * name. Useful for exhaustive switch handling on the TV.
 */
export type TypedCommand = {
    [K in CommandName]: { command: K; payload: CommandPayloads[K] };
}[CommandName];
